import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { forgotPassword, setError } from '../app/redux/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../app/redux/store';
import validator from 'validator';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';
import LanguageSwitcher from '../app/components/LanguageSwitcher';

export default function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState('');
  const [validEmail, setValidEmail] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { error, loading } = useSelector((state: RootState) => state.auth);

  // Email validation
  useEffect(() => {
    if (email) {
      if (validator.isEmail(email)) {
        setValidEmail(true);
      } else {
        setValidEmail(false);
      }
    } else {
      setValidEmail(true);
    }
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !validEmail) {
      dispatch(setError(t('auth:validEmailPlease')));
      return;
    }

    try {
      await dispatch(forgotPassword(email)).unwrap();
      setSubmitted(true);
    } catch (error) {
      logger.error('Failed to send reset code:', error);
    }
  };

  const goBack = () => {
    navigate('/login');
  };

  const continueToReset = () => {
    navigate(`/reset-password?email=${encodeURIComponent(email)}`);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {t('auth:checkYourEmail')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-900">
              {t('auth:sentResetCodeTo')}{' '}
              <span className="font-medium text-indigo-600">{email}</span>
            </p>
            <p className="mt-4 text-center text-sm text-gray-800">
              {t('auth:didntReceiveEmail')}
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={continueToReset}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {t('auth:continueToResetPassword')}
            </button>

            <button
              onClick={handleSubmit}
              className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-900 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {t('auth:resendCode')}
            </button>
          </div>

          <div className="text-center">
            <button
              onClick={goBack}
              className="font-medium text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('auth:backToSignIn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth:forgotPasswordTitle')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-900">
            {t('auth:enterResetCode')}
          </p>
        </div>
        
        {/* Language Switcher */}
        <div className="flex justify-center">
          <LanguageSwitcher />
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {t('common:error')}
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => dispatch(setError(''))}
                      className="text-sm bg-red-50 text-red-800 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      {t('auth:dismiss')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-900">
              {t('auth:emailAddress')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder={t('auth:enterEmailAddress')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {!validEmail && email && (
              <p className="mt-1 text-sm text-red-600">{t('auth:validEmailRequired')}</p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !email || !validEmail}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth:sendingResetLink') : t('auth:sendResetLink')}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={goBack}
              className="font-medium text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('auth:backToSignIn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
